import { FC } from "react";
import * as _ from "lodash";

import { Breadcrumb, Typography, RightOutlined, AntDesignSelect as Select } from "@ctra/components";
import { SectionEntity, SectionList, Enterprise as App } from "@ctra/api";
import { Enterprise, useTranslation } from "@ctra/i18n";
import { Nullable } from "@ctra/utils";
import { useGoogleAnalytics } from "@ctra/analytics";

import { SectionVariant } from "@analytics";
import { CompiledRoutes } from "@routes";

import { GAActions } from "../../../../analytics";
import { testIds } from "../../../../testing";
import styles from "./StackNavigation.module.less";

interface StackNavigationProps {
  /**
   * selected section
   */
  active: Nullable<SectionEntity["id"]>;
  /**
   * list of sections to navigate among
   */
  sections: SectionList;
  /**
   * handle section select
   */
  onSelect: (id: Nullable<SectionEntity["id"]>) => void;
  /**
   * farm/overview variant of the card
   */
  variant?: SectionVariant;
}

const { Link } = Typography;

/**
 * Breadcrumb navigation for the card stack
 * @param variant
 * @param active
 * @param sections
 * @param onSelect
 * @constructor
 */

export const StackNavigation: FC<StackNavigationProps> = ({
  variant = SectionVariant.overview,
  active,
  sections,
  onSelect
}) => {
  const {
    dashboard: { header },
    section: { title: sectionTitle }
  } = Enterprise.layouts;

  const { t } = useTranslation();
  const { trackEvent } = useGoogleAnalytics();

  return (
    <Breadcrumb separator={<RightOutlined />} className={styles.Breadcrumb}>
      <Breadcrumb.Item>
        <Link
          onClick={() => {
            /* istanbul ignore if */
            if (active) {
              trackEvent(GAActions.backTo);
            }

            App.history.push(CompiledRoutes.app.analytics.index());
          }}
        >
          {t<string>(header, { variant })}
        </Link>
      </Breadcrumb.Item>
      {active && (
        <Select
          bordered={false}
          data-testid={testIds.stackNavigation.dropDown}
          dropdownMatchSelectWidth={false}
          onChange={(id) => {
            /* istanbul ignore next */
            trackEvent(GAActions.goToDashboard, { label: sections[id].title });
            onSelect(id);
          }}
          onClick={() => {
            /* istanbul ignore next */
            trackEvent(GAActions.selectDashboard);
          }}
          defaultValue={active}
        >
          {_.map(sections, ({ title }, id) => (
            <Select.Option key={id} value={id}>
              {t<string>(sectionTitle(title), { variant, makeDefaultValue: true })}
            </Select.Option>
          ))}
        </Select>
      )}
    </Breadcrumb>
  );
};
